import type { CSSProperties } from 'react';

import { BIT_ROWS } from './bitRows';

/** The mark's own bytes, flattened into one long deterministic bitstream —
 * same source `Dissolve`/`AmbientBackground` draw from, so this reads as the
 * same signature falling instead of drifting, not a third unrelated
 * texture. Deterministic rather than `Math.random()` for the reason
 * `bitRows.ts` gives: a value that differs between server and client render
 * fails hydration. */
const FLAT_BITS = BIT_ROWS.join('').replace(/[^01]/g, '');

function columnText(index: number, rows: number): string {
  const start = (index * 17) % FLAT_BITS.length;
  const chars: string[] = [];
  for (let r = 0; r < rows; r++) {
    chars.push(FLAT_BITS[(start + r * 7) % FLAT_BITS.length]!);
  }
  return chars.join('\n');
}

export interface BinaryRainProps {
  columns?: number;
  rows?: number;
  className?: string;
  /** CSS `mask-image` value (e.g. `url(/brand/mark-solid.svg)`) — confines
   * the fall to a silhouette instead of a rectangle, the terminal card's
   * watermark treatment. Omit for a plain full-bleed field. */
  mask?: string;
  /**
   * Which of the mock's two binary fields this is.
   *
   * `page` is the one behind the whole site: the deep brand blue, which is
   * the only reason a field this large can sit under body copy without
   * competing with it. `mark` is the terminal card's watermark, in the sky
   * accent, small and confined to the icon's silhouette. They are the same
   * texture at two distances, and swapping the colours makes the background
   * shout and the watermark disappear.
   */
  tone?: 'page' | 'mark';
}

/**
 * Binary digits falling top-to-bottom in columns (Bitwize logo approval
 * mock, 2026-08's `binfall` keyframe) — the site's one ambient texture.
 * `aria-hidden` and non-interactive throughout: decoration, not content.
 */
export function BinaryRain({
  columns = 24,
  rows = 60,
  className,
  mask,
  tone = 'page',
}: BinaryRainProps) {
  const maskStyle: CSSProperties = mask
    ? {
        WebkitMaskImage: mask,
        maskImage: mask,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
      }
    : {};

  return (
    <div
      aria-hidden="true"
      style={maskStyle}
      className={`pointer-events-none flex select-none overflow-hidden ${className ?? ''}`}
    >
      {Array.from({ length: columns }, (_, i) => {
        const duration = 16 + (i % 9) * 2.5;
        const delay = -((i * 3) % 20);

        return (
          <div key={i} className="relative min-w-0 flex-1 overflow-hidden">
            <div
              className={
                tone === 'mark'
                  ? 'hs-binfall-col absolute inset-inline-0 top-0 whitespace-pre-wrap text-center font-mono text-[11px] leading-[1.4] text-[var(--hs-color-accent)]'
                  : 'hs-binfall-col absolute inset-inline-0 top-0 whitespace-pre-wrap text-center font-mono text-[12px] leading-[1.5] text-[var(--hs-brand-700)]'
              }
              style={
                {
                  '--hs-binfall-duration': `${duration}s`,
                  '--hs-binfall-delay': `${delay}s`,
                } as CSSProperties
              }
            >
              {columnText(i, rows)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
